import { useLayoutEffect, useRef, useState } from 'react';
import type { MessageAttachment } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { useTranslation } from '../../../../lib/i18n';
import { docViewerKind, type DocViewerKind } from '../../../../lib/doc-viewer';
import { openDocViewer } from '../../../../lib/doc-viewer-presenter';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { Button, Card } from '../../ui';
import { DocViewerBody } from '../../doc-viewer';
import { attachmentAsFile } from './AttachmentGraphic';
import { AttachmentLabel } from './AttachmentLabel';
import styles from './InlineAttachmentPreview.module.css';

/** How tall a preview may grow before it is clipped and offered an expand. A conversation is a reading
 *  surface first: a long report must not push the reply that follows it off the screen. Past this the
 *  preview fades out and the caption's expand control opens the same document in the full viewer. */
const MAX_H = 360;

/** Kinds that render their own chrome edge-to-edge and look wrong inset — media fills the frame, the rest
 *  is a document body that keeps the frame's padding. */
const BLEEDS: ReadonlySet<DocViewerKind> = new Set<DocViewerKind>(['image', 'video', 'pdf', 'wixel']);

/**
 * A file's CONTENT in the conversation, rather than a chip that opens a window over it.
 *
 * The body is `DocViewerBody` — the same kind-switch the viewer modal renders — so every kind the
 * classifier claims previews here the moment it previews there, with the same loading, unavailable,
 * too-large and error states. Nothing is re-implemented per kind, and the two surfaces cannot drift into
 * disagreeing about the same file.
 *
 * The viewer is not replaced, it is promoted to an escalation: the caption row opens it for a document too
 * long to sit in a bubble, which is what the height cap is for.
 */
export function InlineAttachmentPreview({ att, content }: { att: MessageAttachment; content: string }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [expandable, setExpandable] = useState(false);

  const file = attachmentAsFile(att, content);
  const kind = docViewerKind(att.fileName, content || undefined, att.mimeType);

  // Whether the body is CLIPPED, measured rather than guessed from the kind — the same kind is a paragraph
  // one time and a 40-page report the next. A ResizeObserver rather than a one-shot read because the body
  // grows as its content lands (the markdown/csv/pdf views all fetch), so a measurement taken at mount is
  // taken before there is anything to measure.
  const bodyRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => setExpandable(el.scrollHeight > el.clientHeight + 1);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [content, kind]);

  const open = () => {
    logPageClickBi({ page_name: 'chat', button_name: 'open_file', answer: att.fileName });
    openDocViewer({ file, kind, pageName: 'document_viewer' });
  };

  return (
    <Card variant="outline" size="sm" disablePadding component="figure" className={styles.frame}>
      <div
        ref={bodyRef}
        className={`${styles.body} ${kind && BLEEDS.has(kind) ? styles.bleed : ''}`}
        style={{ maxHeight: MAX_H }}
      >
        <DocViewerBody
          file={file}
          kind={kind}
          onBi={() => undefined}
          onFirstVideoPlay={() =>
            logPageClickBi({ page_name: 'chat', button_name: 'video_played' }, { additionalFields: { file_name: att.fileName } })
          }
        />
        {expandable && <div className={styles.fade} aria-hidden />}
      </div>
      <figcaption className={styles.caption}>
        <AttachmentLabel att={att} content={content} />
        <Button variant="text" size="tiny" className={styles.open} onClick={open}>
          {expandable ? t('common.expand') : t('common.open')}
        </Button>
      </figcaption>
    </Card>
  );
}
